// client/src/pages/Game/Battlefield/hooks/useDevMode.js

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNotification } from '../../../../hooks/useNotification';

/**
 * Hook pour le mode développeur.
 *
 * Les positions sont en POURCENTAGES du frame.
 * Quand l'utilisateur glisse un élément, on convertit le delta en pixels
 * vers un delta en pourcentages en utilisant les dimensions réelles du frame.
 */
const useDevMode = (initialDevMode = false) => {
  const { error: showError, success: showSuccess } = useNotification();
  const [devMode, setDevMode] = useState(initialDevMode);
  const [draggingElement, setDraggingElement] = useState(null);
  const [adminSecret, setAdminSecret] = useState(
    localStorage.getItem('adminSecret') || 'dev-mode-secret'
  );

  // État du panneau de développement
  const [isDevPanelCollapsed, setIsDevPanelCollapsed] = useState(false);
  const [devPanelPosition, setDevPanelPosition] = useState({ x: 20, y: 20 });
  const [isDraggingDevPanel, setIsDraggingDevPanel] = useState(false);
  const [devPanelStartDrag, setDevPanelStartDrag] = useState({ x: 0, y: 0 });

  const devPanelRef = useRef(null);

  // ── Activer / désactiver le mode dev ──
  const toggleDevMode = useCallback(() => {
    setDevMode(prev => !prev);
  }, []);

  const updateAdminSecret = useCallback((newSecret) => {
    setAdminSecret(newSecret);
    localStorage.setItem('adminSecret', newSecret);
  }, []);

  // ── Obtenir les dimensions réelles du frame ──
  const getFrameRect = useCallback(() => {
    const frame = document.querySelector('.battlefield-frame');
    if (!frame) return null;
    return frame.getBoundingClientRect();
  }, []);

  // ── Commencer à déplacer un élément (mode dev) ──
  const handleDevElementDragStart = useCallback((e, elementId) => {
    if (!devMode) return;
    e.stopPropagation();

    let clientX, clientY;
    if (e.type === 'touchstart' && e.touches?.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      e.preventDefault();
    } else if (e.clientX !== undefined) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return;
    }

    setDraggingElement({
      id: elementId,
      startClient: { x: clientX, y: clientY },
    });
  }, [devMode]);

  // ── Déplacer un élément (mode dev) ──
  const handleDevElementDrag = useCallback((e, elementPositions, setElementPositions) => {
    if (!devMode || !draggingElement) return;

    let clientX, clientY;
    if (e.type === 'touchmove' && e.touches?.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      e.preventDefault();
    } else if (e.clientX !== undefined) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return;
    }

    const frameRect = getFrameRect();
    if (!frameRect || frameRect.width === 0 || frameRect.height === 0) return;

    // Delta en pixels
    const deltaXpx = clientX - draggingElement.startClient.x;
    const deltaYpx = clientY - draggingElement.startClient.y;

    // Convertir en pourcentages du frame
    const deltaXpct = (deltaXpx / frameRect.width) * 100;
    const deltaYpct = (deltaYpx / frameRect.height) * 100;

    const currentPos = elementPositions[draggingElement.id] || { x: 50, y: 50 };
    const newX = Math.max(0, Math.min(100, currentPos.x + deltaXpct));
    const newY = Math.max(0, Math.min(100, currentPos.y + deltaYpct));

    setElementPositions(prev => ({
      ...prev,
      [draggingElement.id]: { x: parseFloat(newX.toFixed(2)), y: parseFloat(newY.toFixed(2)) },
    }));

    // Mettre à jour la position de départ
    setDraggingElement(prev => ({
      ...prev,
      startClient: { x: clientX, y: clientY },
    }));
  }, [devMode, draggingElement, getFrameRect]);

  // ── Fin du déplacement (mode dev) ──
  const handleDevElementDragEnd = useCallback(async (elementPositions, elementSizes, saveToLocal) => {
    if (!devMode || !draggingElement) return;

    // Sauvegarder dans localStorage
    if (saveToLocal) {
      saveToLocal(elementPositions, elementSizes);
    } else {
      try {
        localStorage.setItem('elementPositions_pct', JSON.stringify(elementPositions));
        localStorage.setItem('elementSizes_pct', JSON.stringify(elementSizes));
      } catch (e) {
        console.warn('useDevMode: erreur sauvegarde', e);
      }
    }

    setDraggingElement(null);
  }, [devMode, draggingElement]);

  // ── Réinitialiser les positions ──
  const resetElementPositions = useCallback((setElementPositions) => {
    const defaults = {
      playerHand:       { x: 50, y: 95 },
      opponentHand:     { x: 50, y: 5 },
      playerMonsters:   { x: 50, y: 70 },
      opponentMonsters: { x: 50, y: 30 },
      playerDeck:       { x: 10, y: 88 },
      opponentDeck:     { x: 90, y: 12 },
      playerLife:       { x: 88, y: 88 },
      opponentLife:     { x: 12, y: 12 },
      ActionButton:     { x: 50, y: 83 },
      playerBattleZone:   { x: 50, y: 58 },
      opponentBattleZone: { x: 50, y: 42 },
    };

    if (setElementPositions) {
      setElementPositions({ ...defaults });
    }

    try {
      localStorage.removeItem('elementPositions_pct');
      localStorage.removeItem('elementSizes_pct');
    } catch (e) { /* ignore */ }

    showSuccess('Positions réinitialisées');
    return defaults;
  }, [showSuccess]);

  // ── Mise à jour manuelle position ──
  const updateElementPosition = useCallback((elementId, axis, value, setElementPositions) => {
    const numVal = parseFloat(value) || 0;
    setElementPositions(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [axis]: Math.max(0, Math.min(100, numVal)),
      },
    }));
  }, []);

  // ── Mise à jour manuelle taille ──
  const updateElementSize = useCallback((elementId, dimension, value, setElementSizes) => {
    const numVal = parseFloat(value) || 0;
    // width/height : 1..100 cqw. Autres dimensions (crystalSize, pearlSize,
    // …) : minimum abaissé à 0.2 pour les petits orbes autour des PV.
    const min = (dimension === 'width' || dimension === 'height') ? 1 : 0.2;
    setElementSizes(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [dimension]: Math.max(min, Math.min(100, numVal)),
      },
    }));
  }, []);

  // ── Panneau dev: drag ──
  const handleDevPanelDragStart = useCallback((e) => {
    if (!devPanelRef.current) return;
    setIsDraggingDevPanel(true);

    let clientX, clientY;
    if (e.type === 'touchstart' && e.touches?.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      e.preventDefault();
    } else if (e.clientX !== undefined) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return;
    }

    setDevPanelStartDrag({
      x: clientX - devPanelPosition.x,
      y: clientY - devPanelPosition.y,
    });
    e.stopPropagation();
  }, [devPanelPosition]);

  const handleDevPanelDrag = useCallback((e) => {
    if (!isDraggingDevPanel) return;

    let clientX, clientY;
    if (e.type === 'touchmove' && e.touches?.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
      e.preventDefault();
    } else if (e.clientX !== undefined) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      return;
    }

    setDevPanelPosition({
      x: clientX - devPanelStartDrag.x,
      y: clientY - devPanelStartDrag.y,
    });
  }, [isDraggingDevPanel, devPanelStartDrag]);

  const handleDevPanelDragEnd = useCallback(() => {
    setIsDraggingDevPanel(false);
  }, []);

  // ── Configurer les handlers globaux pour le drag (mousemove/touchmove sur le container) ──
  const setupDevDragHandlers = useCallback((container, elementPositions, setElementPositions, elementSizes, saveToLocal) => {
    if (!container || !devMode) return { cleanup: () => {} };

    const handleMouseMove = (e) => {
      if (draggingElement) {
        handleDevElementDrag(e, elementPositions, setElementPositions);
      } else if (isDraggingDevPanel) {
        handleDevPanelDrag(e);
      }
    };

    const handleMouseUp = () => {
      if (draggingElement) {
        handleDevElementDragEnd(elementPositions, elementSizes, saveToLocal);
      } else if (isDraggingDevPanel) {
        handleDevPanelDragEnd();
      }
    };

    const handleTouchMove = (e) => {
      if (draggingElement) {
        handleDevElementDrag(e, elementPositions, setElementPositions);
      } else if (isDraggingDevPanel) {
        handleDevPanelDrag(e);
      }
    };

    const handleTouchEnd = () => {
      if (draggingElement) {
        handleDevElementDragEnd(elementPositions, elementSizes, saveToLocal);
      } else if (isDraggingDevPanel) {
        handleDevPanelDragEnd();
      }
    };

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return {
      cleanup: () => {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      },
    };
  }, [
    devMode,
    draggingElement,
    isDraggingDevPanel,
    handleDevElementDrag,
    handleDevElementDragEnd,
    handleDevPanelDrag,
    handleDevPanelDragEnd,
  ]);

  // Sauvegarder l'état du mode développeur
  useEffect(() => {
    if (devMode) {
      localStorage.setItem('devMode', 'true');
    } else {
      localStorage.removeItem('devMode');
    }
  }, [devMode]);

  return {
    devMode,
    setDevMode,
    toggleDevMode,
    draggingElement,
    adminSecret,
    updateAdminSecret,
    handleDevElementDragStart,
    handleDevElementDrag,
    handleDevElementDragEnd,
    resetElementPositions,
    updateElementPosition,
    updateElementSize,
    isDevPanelCollapsed,
    setIsDevPanelCollapsed,
    devPanelPosition,
    isDraggingDevPanel,
    handleDevPanelDragStart,
    handleDevPanelDrag,
    handleDevPanelDragEnd,
    devPanelRef,
    setupDevDragHandlers,
  };
};

export default useDevMode;
